import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { STATS } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import AnimatedBackground from "@/components/AnimatedBackground";

const Counter = ({ value, suffix, decimals }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const reduce = useReducedMotion();
    const [display, setDisplay] = useState(reduce ? value : 0);

    useEffect(() => {
        if (!inView || reduce) return;
        const duration = 1600;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const t = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(value * eased);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value, reduce]);

    return (
        <span ref={ref}>
            {display.toFixed(decimals)}
            {suffix}
        </span>
    );
};

const Stats = () => (
    <section data-testid="stats-section" className="border-y t-border t-bg-alt py-16 md:py-24">
        <div className="container-x grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
            {STATS.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                    <div data-testid={`stat-${i + 1}`}>
                        <p className="font-display text-4xl font-bold tracking-tight t-heading md:text-5xl lg:text-6xl">
                            <Counter value={s.value} suffix={s.suffix} decimals={s.decimals} />
                        </p>
                        <p className="mt-3 max-w-[16rem] text-sm leading-relaxed t-soft">{s.label}</p>
                    </div>
                </Reveal>
            ))}
        </div>
    </section>
);

const FinalCTA = () => (
    <section data-testid="final-cta-section" className="media-dark bg-grid-dark relative overflow-hidden t-bg py-16 md:py-36">
        <img
            src="/backgrounds/12-cta-footer.webp"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.22]"
        />
        <AnimatedBackground className="absolute inset-0 h-full w-full opacity-60" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[30rem] w-[46rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-electric/10 blur-[130px]" />
        <div className="animate-beam pointer-events-none absolute inset-y-[30%] left-[-10%] h-[2px] w-[40%] bg-gradient-to-r from-transparent via-electric/70 to-transparent" />
        <div className="container-x relative z-10 text-center">
            <Reveal>
                <p className="eyebrow">Start the Conversation</p>
                <h2 className="mx-auto mt-6 max-w-4xl font-display text-3xl font-bold leading-[1.08] tracking-tight t-heading sm:text-4xl lg:text-6xl">
                    Ready to transform <span className="text-gradient">what's possible?</span>
                </h2>
                <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed t-soft md:text-lg">
                    Let's discuss your technology priorities and build a practical path forward — together.
                </p>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                    <Link to="/contact" data-testid="final-cta-talk-btn" className="btn-primary">
                        Talk to Varasun <ArrowRight size={16} />
                    </Link>
                    <Link to="/#capabilities" data-testid="final-cta-services-btn" className="btn-ghost">
                        Explore Our Services
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>
);

export { Stats, FinalCTA };
